import { Component, computed, effect, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, take } from 'rxjs';
import {
  AppShellRootComponent,
  injectShellRouteData,
  type ShellNavEntry,
} from '@sbe-spinoff-client/app-shell';
import {
  injectSpinoffBootLoading,
  SpinoffAuthService,
  SpinoffProfileMenuComponent,
} from '@sbe-spinoff-client/spinoff-platform';
import { ConductViewportService } from './core/layout/conduct-viewport.service';
import { appRoutes } from './app.routes';
import { REQUIRE_LOGIN } from './core/auth/require-login.token';
import { ConductActiveOrgService } from './core/org/conduct-active-org.service';
import { ConductMySeatsService } from './core/org/conduct-my-seats.service';

@Component({
  selector: 'app-root',
  imports: [AppShellRootComponent, RouterLink, SpinoffProfileMenuComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  readonly auth = inject(SpinoffAuthService);
  readonly activeOrg = inject(ConductActiveOrgService);
  readonly mySeats = inject(ConductMySeatsService);
  private readonly router = inject(Router);
  private readonly requireLogin = inject(REQUIRE_LOGIN);
  private readonly viewport = inject(ConductViewportService);
  // Set per route in app.routes.ts.
  private readonly routeData = injectShellRouteData({
    authPage: false,
    bare: false,
    mobileBare: false,
  });
  // Until the first navigation ends there is no page yet (router.url is still '/').
  // router.navigated is not a signal, so the session watch reads this one to run again then.
  private readonly firstPageOpened = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => true),
      take(1),
    ),
    { initialValue: false },
  );

  private readonly staffNavItems = computed<ShellNavEntry[]>(() => [
    {
      label: $localize`:@@conductNavHome:홈`,
      routerLink: `${this.activeOrg.schoolPath()}/dashboard`,
      exact: true,
      icon: 'home',
    },
    {
      label: $localize`:@@conductNavGrant:상벌점 부여`,
      routerLink: `${this.activeOrg.schoolPath()}/entries/grant`,
      icon: 'add',
    },
    {
      label: $localize`:@@conductNavHistory:상벌점 조회`,
      routerLink: `${this.activeOrg.schoolPath()}/entries/history`,
      icon: 'list',
    },
    {
      label: $localize`:@@conductNavMembers:구성원`,
      routerLink: `${this.activeOrg.schoolPath()}/members`,
      icon: 'group',
    },
    {
      label: $localize`:@@conductNavSettings:설정`,
      routerLink: `${this.activeOrg.schoolPath()}/settings`,
      icon: 'settings',
    },
  ]);

  readonly studentHome = computed(() => {
    const orgId = this.activeOrg.orgId();

    return (
      !!orgId &&
      this.mySeats.loadedOrgId() === orgId &&
      !this.mySeats.seatsFailed() &&
      !this.mySeats.isFaculty()
    );
  });

  // Without a school (sign-in pages, /connect) there is no school path to link to:
  // /dashboard opens the last school or the connect page.
  readonly homeRoute = computed(() => {
    if (!this.activeOrg.orgId()) return '/dashboard';
    if (this.studentHome()) return `${this.activeOrg.schoolPath()}/me`;

    return `${this.activeOrg.schoolPath()}/dashboard`;
  });

  // Until the seats answer, a student would briefly see the staff menu.
  private readonly roleLoading = computed(() => {
    const orgId = this.activeOrg.orgId();

    if (!orgId || !this.auth.isAuthenticated()) return false;
    if (this.mySeats.seatsFailed()) return false;

    return this.mySeats.loadedOrgId() !== orgId;
  });

  readonly navItems = computed<ShellNavEntry[]>(() => {
    if (!this.activeOrg.orgId() || this.roleLoading()) return [];

    if (this.studentHome()) {
      return [
        {
          label: $localize`:@@conductMeNav:내 상벌점`,
          routerLink: `${this.activeOrg.schoolPath()}/me`,
          exact: true,
          icon: 'person',
        },
      ];
    }

    // A teacher who is also a guardian can open their child's page too.
    if (this.mySeats.studentSeats().length > 0) {
      return [
        ...this.staffNavItems(),
        {
          label: $localize`:@@conductNavChildren:자녀 상벌점`,
          routerLink: `${this.activeOrg.schoolPath()}/me`,
          exact: true,
          icon: 'person',
        },
      ];
    }

    return this.staffNavItems();
  });

  // Shows a loading state in the content area while a guarded cold entry restores the session.
  readonly showBootLoading = injectSpinoffBootLoading(appRoutes);

  private readonly compact = this.viewport.compact;

  readonly authPage = computed(() => this.routeData().authPage);

  readonly shellBare = computed(() => {
    return this.authPage() || this.routeData().bare || this.compactPage();
  });

  readonly compactPage = computed(() => {
    return this.compact() && this.routeData().mobileBare;
  });

  readonly containerClass = computed(() => {
    if (this.compactPage()) return 'conduct-mobile-content';

    return 'conduct-content';
  });

  constructor() {
    // The sidenav shows the school's name; it is read once signed in and again when the school changes.
    // Not on sign-in pages: a 학교종이 「진입」 signs in there before it selects the entered school.
    effect(() => {
      const orgId = this.activeOrg.orgId();

      if (!orgId || this.authPage() || !this.auth.isAuthenticated()) return;

      void this.activeOrg.refresh(orgId);
    });

    // The menus depend on the role, so read the seats as soon as a school and a session are
    // known, and again after they are dropped (another person entered in this tab).
    effect(() => {
      const orgId = this.activeOrg.orgId();

      if (!orgId || this.authPage() || !this.auth.isAuthenticated()) return;
      if (this.mySeats.loadedOrgId() === orgId || this.mySeats.seatsFailed()) {
        return;
      }

      untracked(() => void this.mySeats.ensure(orgId));
    });

    // The guard only runs on navigation. When the session is lost on a conduct page (refresh
    // token expired, signed out elsewhere), send the visitor to login and back afterwards.
    // Wait for the first page: before it opens router.url is still '/', and a guard that is
    // still deciding sends a signed-out visitor to login with the right return itself.
    effect(() => {
      if (!this.requireLogin || this.authPage()) return;
      if (!this.auth.ready() || this.auth.isAuthenticated()) return;
      if (!this.firstPageOpened()) return;

      void this.router.navigate([this.auth.loginPath], {
        queryParams: { return: this.router.url },
      });
    });
  }

  // The shell's outage screen: try again from a fresh page.
  retry() {
    window.location.reload();
  }

  // Go to the sign-in page, then end the session. In the other order the
  // session watch above would see a signed-out conduct page and add a
  // ?return= link back to it. If a guard keeps the page, stay signed in.
  async logout() {
    const left = await this.router.navigateByUrl('/');

    if (!left) return;

    await this.auth.signOut();
    this.activeOrg.clear();
    this.mySeats.clear();
  }
}
